import {
  CITY_LIST_URLS,
  FBN_LOOKBACK_DAYS,
  FBN_SEARCH_URL,
  HDL_BROWSER_HOSTS,
  INTAKE_ADDRESS,
  OCHCA_PLAN_REVIEW_URL,
} from '../../../../shared/fields';
import { SOURCE_NAMES } from '../../logic/signals';
import { shell } from '../../app';
import { planCheckBookmarklet } from '../../logic/bookmarklet';
import { h } from '../../ui/dom';

/** How to bring in OC Health plan checks (D58): the portal only lets people in, so the rep's own
 *  browser reads it with a bookmark set up once from here. Desktop browser; sign in to this app there.
 *  The usual slips — clicking the button here, ⌘D (bookmarks this page instead), clicking the bookmark
 *  on a blank tab — are named in the steps, and clicking the button here explains itself. */
export const planCheckHowTo = (): HTMLDetailsElement =>
  h(
    'details',
    { class: 'section sig-howto' },
    h('summary', { class: 'section__title' }, 'Send to CRM: OC Health, the county and 9 cities'),
    h(
      'p',
      { class: 'card__summary' },
      'New restaurants, markets and food trucks file plans with OC Health months before they open, and nine cities list their new licenses on sites that only let people in. A bookmark in your own browser brings them here. On your computer, in Chrome:',
    ),
    h(
      'ol',
      { class: 'sig-howto__steps' },
      h('li', {}, 'Show the bookmarks bar: ⌘ Shift B.'),
      h(
        'li',
        {},
        'Drag this button up onto the bookmarks bar (dragging, not ⌘D — that would bookmark this page): ',
        h(
          'a',
          {
            class: 'btn btn--subtle btn--sm sig-howto__bookmark',
            href: planCheckBookmarklet(location.origin),
            title: 'Drag me to the bookmarks bar',
            onClick: (ev: Event) => {
              ev.preventDefault(); // it only works from the bookmarks bar, on the OC Health page
              shell.toast(
                'Drag this button to the bookmarks bar — it works from there, on the OC Health page.',
                {
                  duration: 6000,
                },
              );
            },
          },
          'Send to CRM',
        ),
      ),
      h(
        'li',
        {},
        'Open the ',
        h(
          'a',
          { href: OCHCA_PLAN_REVIEW_URL, target: '_blank', rel: 'noopener' },
          'OC Health plan-review page',
        ),
        ' and, on that page, click “Send to CRM” in the bookmarks bar.',
      ),
      h(
        'li',
        {},
        'For the county’s filings, open the ',
        h('a', { href: FBN_SEARCH_URL, target: '_blank', rel: 'noopener' }, 'business-name search'),
        ` and click “Send to CRM” there: it reads the last ${FBN_LOOKBACK_DAYS} days of filings (a minute or two) and opens what is worth a look.`,
      ),
      h(
        'li',
        {},
        `A list you downloaded yourself (Costa Mesa, Laguna Beach) can also be emailed to ${INTAKE_ADDRESS} from your own address, with the city's name in the subject: it imports itself, and anything the reader cannot read comes back to your mailbox.`,
      ),
      h(
        'li',
        {},
        'A new tab opens here with the plan checks of the last 60 days: tap Import. Repeats are skipped.',
      ),
      h(
        'li',
        {},
        "Once a month, do the same on each city's license search page (click Send to CRM there): ",
        ...(Object.keys(HDL_BROWSER_HOSTS) as (keyof typeof HDL_BROWSER_HOSTS)[]).flatMap((city, i) => [
          i ? ' · ' : '',
          h(
            'a',
            {
              href:
                city === 'laguna_beach'
                  ? `${HDL_BROWSER_HOSTS.laguna_beach}/Search/Index/BusinessLicense`
                  : CITY_LIST_URLS[city],
              target: '_blank',
              rel: 'noopener',
            },
            SOURCE_NAMES[city],
          ),
        ]),
        '. It brings the licenses started in the last 45 days.',
      ),
    ),
  );
